import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import {
  Box, Text, Layer, TextInput,
} from 'grommet';
import { useAppTheme } from '@Components/Context';
import {
  Seo, InfoNotice, AppButton, DropButton, FlexibleSelectDropdown, SimpleModal,
} from '@Components/Control';
import { AdminNavWrapper } from '@Components/Navigation';
import { buildDateTimeLabel } from '@Helpers/util';
import {
  initiateFetchPayouts,
  clearPayouts,
  initiateUpdatePayout,
  initiateTriggerPayout,
  initiateFetchPayoutHistory,
  fetchCompanyListRequest,
} from '@Actions';
import {
  TableWrapper,
  NoDataPlaceholder,
  tablePadding,
  renderTableHeader,
} from '../../../Partial/Product/SharedComponents';
import { StyledDropMenu } from '../../../Partial/Product/Dashboard/StyledDashboard';

const PAGE_SIZE = 50;

const currentYear = new Date().getFullYear();
const YEAR_OPTIONS = Array.from(
  { length: currentYear - 2023 },
  (_, i) => {
    const year = String(2024 + i);
    return { api: year, display: year };
  },
);

const MONTH_OPTIONS = [
  { api: '01', display: 'January' },
  { api: '02', display: 'February' },
  { api: '03', display: 'March' },
  { api: '04', display: 'April' },
  { api: '05', display: 'May' },
  { api: '06', display: 'June' },
  { api: '07', display: 'July' },
  { api: '08', display: 'August' },
  { api: '09', display: 'September' },
  { api: '10', display: 'October' },
  { api: '11', display: 'November' },
  { api: '12', display: 'December' },
];

const STATUS_OPTIONS = [
  { api: 'PENDING', display: 'Pending' },
  { api: 'PROCESSING', display: 'Processing' },
  { api: 'PAID', display: 'Paid' },
  { api: 'FAILED', display: 'Failed' },
];

const STATUS_STYLES = {
  PENDING: { bg: '#f6ad5522', color: '#f6ad55', border: '#f6ad55' },
  PROCESSING: { bg: '#3b82f622', color: '#3b82f6', border: '#3b82f6' },
  PAID: { bg: null, color: '#fff', border: null },
  FAILED: { bg: null, color: '#fff', border: null },
};

const HEADER = [
  { display: 'Month', uid: 'month', size: '7%' },
  { display: 'Company', uid: 'customerName', size: '18%' },
  { display: 'Status', uid: 'status', size: '7%' },
  { display: 'Estimated Revenue', uid: 'estimatedRevenue', size: '9%' },
  { display: 'Total Revenue', uid: 'totalRevenue', size: '9%' },
  { display: 'RF Share', uid: 'redflagShare', size: '8%' },
  { display: 'Payable', uid: 'payableRevenue', size: '8%' },
  { display: 'Referral Bonus', uid: 'referralBonus', size: '8%' },
  { display: 'Total Payout', uid: 'totalPayout', size: '8%' },
  { display: 'Paid At', uid: 'paidAt', size: '8%' },
  { display: '', uid: 'actions', size: '9%' },
];

const EMPTY_EDIT_FIELDS = { referralBonus: '', assetUpdates: {} };

function formatMonth(value) {
  if (!value) return '—';
  const [year, month] = value.split('-');
  if (!month) return value;
  const date = new Date(Number(year), Number(month) - 1, 1);
  return date.toLocaleString('en-US', { month: 'long', year: 'numeric' });
}

function formatMoney(value) {
  if (!value) return '—';
  return `$${Number(value).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function formatPaidAt(value) {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString('en-US', {
    month: 'short',
    day: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
  });
}

function toSnakeUpdates(fields) {
  const updates = {};
  const referral = parseFloat(fields.referralBonus);
  // eslint-disable-next-line camelcase
  if (!Number.isNaN(referral)) updates.referral_bonus = referral;

  const assetUpdates = Object.entries(fields.assetUpdates || {}).reduce((acc, [label, value]) => {
    const amount = parseFloat(value);
    // eslint-disable-next-line camelcase
    if (!Number.isNaN(amount)) acc.push({ asset_label: label, total_revenue: amount });
    return acc;
  }, []);
  // eslint-disable-next-line camelcase
  if (assetUpdates.length > 0) updates.asset_updates = assetUpdates;

  return updates;
}

function computeEditTotal(fields, companyRevSharePercent) {
  const referral = parseFloat(fields.referralBonus) || 0;
  const revShare = companyRevSharePercent || 0;
  const labelSum = Object.values(fields.assetUpdates || {}).reduce((sum, v) => {
    const n = parseFloat(v);
    if (Number.isNaN(n)) return sum;
    return sum + (n * (1 - revShare / 100));
  }, 0);
  return labelSum + referral;
}

const AdminPayoutDashboardPage = ({
  small,
  location,
  fetchPayoutsData,
  clearPayoutsData,
  updatePayout,
  triggerPayout,
  fetchPayoutHistory,
  fetchCompanyList,
  loading,
  submitting,
  payoutsData = null,
  companyList = null,
}) => {
  const { theme: { page, button }, isDarkMode } = useAppTheme();
  const usePageBg = isDarkMode ? page.background : page.altBg;

  const [currentPage, setCurrentPage] = React.useState(0);
  const [selectedYear, setSelectedYear] = React.useState('');
  const [selectedMonth, setSelectedMonth] = React.useState('');
  const [selectedCompanyId, setSelectedCompanyId] = React.useState(null);
  const [selectedStatus, setSelectedStatus] = React.useState('');
  const [confirmEntry, setConfirmEntry] = React.useState(null);
  const [editEntry, setEditEntry] = React.useState(null);
  const [editFields, setEditFields] = React.useState(EMPTY_EDIT_FIELDS);
  const [breakdownEntry, setBreakdownEntry] = React.useState(null);
  const [rfShareEntry, setRfShareEntry] = React.useState(null);
  const [historyEntry, setHistoryEntry] = React.useState(null);
  const [historyLogs, setHistoryLogs] = React.useState([]);
  const [historyLoading, setHistoryLoading] = React.useState(false);

  const companyOptions = React.useMemo(
    () => (companyList || []).map(({ id, name }) => ({ api: id, display: name })),
    [companyList],
  );

  React.useEffect(() => {
    fetchCompanyList();
  }, [fetchCompanyList]);

  const queryParams = React.useMemo(() => {
    const p = { limit: PAGE_SIZE, skip: currentPage * PAGE_SIZE };
    if (selectedYear && selectedMonth) p.month = `${selectedYear}-${selectedMonth}`;
    else if (selectedYear) p.month = selectedYear;
    if (selectedStatus) p.status = selectedStatus;
    // eslint-disable-next-line camelcase
    if (selectedCompanyId) p.company_id = selectedCompanyId;
    return p;
  }, [currentPage, selectedYear, selectedMonth, selectedStatus, selectedCompanyId]);

  React.useEffect(
    () => { setCurrentPage(0); },
    [selectedYear, selectedMonth, selectedStatus, selectedCompanyId],
  );

  React.useEffect(() => {
    fetchPayoutsData(queryParams);
    return () => { clearPayoutsData(); };
  }, [queryParams, fetchPayoutsData]);

  const handleReset = () => {
    setSelectedYear('');
    setSelectedMonth('');
    setSelectedCompanyId(null);
    setSelectedStatus('');
    setCurrentPage(0);
  };

  const handlePage = (pageNum) => setCurrentPage(pageNum);

  const handleEditOpen = (row) => {
    setEditEntry(row);
    const assetUpdates = {};
    (row.assetBreakdown || []).forEach((a) => {
      assetUpdates[a.assetLabel] = String(a.totalRevenue ?? 0);
    });
    setEditFields({
      referralBonus: String(row.referralBonus ?? ''),
      assetUpdates,
    });
  };

  const handleEditSave = () => {
    if (!editEntry) return;
    const updates = toSnakeUpdates(editFields);
    if (Object.keys(updates).length === 0) return;
    updatePayout(editEntry.id, updates, () => {
      setEditEntry(null);
      setEditFields(EMPTY_EDIT_FIELDS);
      fetchPayoutsData(queryParams);
    });
  };

  const handleHistoryOpen = (row) => {
    setHistoryEntry(row);
    setHistoryLogs([]);
    setHistoryLoading(true);
    fetchPayoutHistory(row.id, (logs) => {
      setHistoryLogs(logs);
      setHistoryLoading(false);
    });
  };

  const handlePayConfirm = () => {
    if (!confirmEntry) return;
    triggerPayout(confirmEntry.id, () => {
      setConfirmEntry(null);
      fetchPayoutsData(queryParams);
    });
  };

  const { data = [], pagination = {}, totals = {} } = payoutsData || {};
  const { total: totalResults = 0, skip = 0, limit = PAGE_SIZE } = pagination;
  const {
    totalRevenue: totalRevenueStat = 0,
    estimatedRevenue: estimatedRevenueStat = 0,
    redflagShare: redflagShareStat = 0,
    payableRevenue: payableRevenueStat = 0,
  } = totals;

  const hasActiveFilters = !!(selectedYear || selectedMonth || selectedStatus || selectedCompanyId);

  const renderCell = (uid, row) => {
    if (uid === 'status') {
      const style = STATUS_STYLES[row.status] || {};
      const useSuccess = row.status === 'PAID';
      const useError = row.status === 'FAILED';
      const { bg: defaultBg, border: defaultBorder } = style;
      let bg = defaultBg;
      let border = defaultBorder;
      if (useSuccess) { bg = page.statusSuccess; border = page.statusSuccess; }
      if (useError) { bg = page.statusError; border = page.statusError; }
      const color = useSuccess || useError ? '#fff' : style.color;
      return (
        <Box
          background={bg}
          border={{ color: border, size: '1px' }}
          round="6px"
          pad={{ vertical: '0.2rem', horizontal: '0.5rem' }}
          width="fit-content"
        >
          <Text size="0.8rem" weight={600} color={color}>
            {row.status || '—'}
          </Text>
        </Box>
      );
    }

    if (uid === 'month') {
      return (
        <Text size="0.9rem" color={page.tableText}>
          {formatMonth(row.month)}
        </Text>
      );
    }

    if (uid === 'estimatedRevenue') {
      return (
        <Box direction="column">
          <Text size="1rem" color={page.tableText}>
            {formatMoney(row.estimatedRevenue)}
          </Text>
          {row.estimatedDaysCovered > 0 && (
            <Text size="0.75rem" color={page.secondaryText}>
              {`${row.estimatedDaysCovered}/${row.estimatedDaysExpected} days`}
            </Text>
          )}
        </Box>
      );
    }

    if (uid === 'redflagShare') {
      const hasDeduction = row.referralDeduction > 0;
      const netRfShare = (row.redflagShare || 0) - (row.referralDeduction || 0);
      return (
        <Box direction="column" gap="0.15rem">
          <Text size="1rem" color={page.tableText}>
            {formatMoney(hasDeduction ? netRfShare : row.redflagShare)}
          </Text>
          {hasDeduction && (
            <Text
              as="span"
              size="0.75rem"
              color={page.highlight}
              style={{ textDecoration: 'underline', cursor: 'pointer' }}
              role="button"
              tabIndex={0}
              onClick={() => setRfShareEntry(row)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  setRfShareEntry(row);
                }
              }}
            >
              See breakdown
            </Text>
          )}
        </Box>
      );
    }

    if (uid === 'totalRevenue' || uid === 'payableRevenue' || uid === 'referralBonus' || uid === 'totalPayout') {
      return (
        <Text size="1rem" color={page.tableText}>
          {formatMoney(row[uid])}
        </Text>
      );
    }

    if (uid === 'paidAt') {
      return (
        <Text size="0.9rem" color={page.tableText}>
          {formatPaidAt(row.paidAt)}
        </Text>
      );
    }

    if (uid === 'customerName') {
      const displayName = row.customerName
        || companyOptions.find((o) => String(o.api) === String(row.companyId))?.display
        || `Company ${row.companyId}`;
      const assetCount = row.assetBreakdown?.length ?? 0;
      const hasBreakdown = assetCount > 1;
      return (
        <Box direction="column" gap="0.25rem">
          <Text size="1rem" color={page.tableText}>{displayName}</Text>
          <Text size="0.75rem" color={page.tableHeader}>{`ID: ${row.companyId}`}</Text>
          {hasBreakdown && (
            <Text size="0.9rem" color={page.tableText} style={{ lineHeight: 1.45 }}>
              <Text weight={600} as="span" color={page.tableText}>{assetCount}</Text>
              {' labels · '}
              <Text
                as="span"
                size="0.9rem"
                color={page.highlight}
                style={{ textDecoration: 'underline', cursor: 'pointer' }}
                role="button"
                tabIndex={0}
                onClick={() => setBreakdownEntry(row)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    setBreakdownEntry(row);
                  }
                }}
              >
                Asset Label Breakdown
              </Text>
            </Text>
          )}
        </Box>
      );
    }

    if (uid === 'actions') {
      const isPending = row.status === 'PENDING';
      const isFailed = row.status === 'FAILED';
      const isPaid = row.status === 'PAID';
      const canPay = isPending || isFailed;
      return (
        <DropButton
          withCloseDropContent={(onClose) => (
            <Box round="var(--app-radius)" pad="small" background={button.tableBg}>
              <StyledDropMenu
                plain
                label="Edit"
                disabled={!isPending || submitting}
                textColor={isPending ? page.tableText : page.hintText}
                hoverTextColor={button.hoverText}
                hoverIndicator={isPending ? button.hoverBg : 'none'}
                onClick={() => { handleEditOpen(row); onClose(); }}
              />
              <StyledDropMenu
                plain
                label={isFailed ? 'Retry Pay' : 'Pay'}
                disabled={!canPay || submitting}
                textColor={canPay ? page.tableText : page.hintText}
                hoverTextColor={button.hoverText}
                hoverIndicator={canPay ? button.hoverBg : 'none'}
                onClick={() => { setConfirmEntry(row); onClose(); }}
              />
              {isPaid && row.receiptUrl && (
                <StyledDropMenu
                  plain
                  label="View Receipt"
                  textColor={page.tableText}
                  hoverTextColor={button.hoverText}
                  hoverIndicator={button.hoverBg}
                  onClick={() => { window.open(row.receiptUrl, '_blank'); onClose(); }}
                />
              )}
              <StyledDropMenu
                plain
                label="History"
                textColor={page.tableText}
                hoverTextColor={button.hoverText}
                hoverIndicator={button.hoverBg}
                onClick={() => { handleHistoryOpen(row); onClose(); }}
              />
            </Box>
          )}
        >
          {(Icon) => (
            <AppButton
              dropDown
              tableButtonStyle
              color={button.tableText}
              focusHighlight={page.focusHighlight}
              textSize="1rem"
              fontWeight={600}
              label="Actions"
              icon={<Icon color={button.tableIcon} size="0.9rem" />}
            />
          )}
        </DropButton>
      );
    }

    return (
      <Text size="1rem" color={page.tableText}>
        {row[uid] || '—'}
      </Text>
    );
  };

  const renderContents = () => {
    if (loading || data?.length === 0) {
      return (
        <NoDataPlaceholder
          noShadow
          noRound
          showRefreshButton={data?.length === 0 && hasActiveFilters}
          refreshQuery={handleReset}
          buttonHighlight={button.primaryBg}
          loading={loading}
          label="Loading payout entries..."
          noDataLabel="No payout entries found. Try adjusting your filters."
          textColor={page.primaryText}
          loaderColor={page.highlight}
        />
      );
    }

    return (
      <Box fill="horizontal" direction="column">
        {!small && (
          <Box
            fill="horizontal"
            wrap={false}
            direction="row"
            border={{
              color: page.tableBorder, size: '1px', style: 'solid', side: 'bottom',
            }}
          >
            {HEADER.map(({ uid, display, size }) => (
              <Box
                key={uid}
                width={size}
                pad={{ horizontal: '0.5rem', top: '1rem', bottom: '0.5rem' }}
              >
                {renderTableHeader(display, small)}
              </Box>
            ))}
          </Box>
        )}
        {data.map((row) => (
          <Box
            key={row.id}
            direction={small ? 'column' : 'row'}
            align={small ? 'start' : 'center'}
            border={{
              color: page.tableBorder, size: '1px', style: 'solid', side: 'bottom',
            }}
            pad={{ vertical: '1rem', right: '0.5rem' }}
            background={page.tableBg}
            fill="horizontal"
          >
            {HEADER.map(({ uid, display, size }) => (
              <Box
                key={uid}
                width={small ? '100%' : size}
                pad={{ horizontal: small ? '0' : '0.5rem', vertical: small ? '0.25rem' : '0' }}
                direction="row"
                gap="0.5rem"
              >
                {small && (
                  <Text size="0.875rem" color={page.tableHeader}>
                    {display}
                    :
                  </Text>
                )}
                {renderCell(uid, row)}
              </Box>
            ))}
          </Box>
        ))}
      </Box>
    );
  };

  const tableControls = (
    <Box wrap direction="row" gap="0.5rem" align="center">
      <Box pad={{ vertical: small ? '0.5rem' : '0' }}>
        <FlexibleSelectDropdown
          textColor={page.inputText}
          handleItemSelect={(value) => setSelectedYear(value || '')}
          flexibleOptions={YEAR_OPTIONS}
          noSelectionLabel="All years"
          displayKey="display"
          valueKey="api"
          iconPath="images/filter-input-icon.svg"
          customPad="0.2rem 0.3rem"
          selectedOption={YEAR_OPTIONS.find((o) => o.api === selectedYear)?.display || 'All years'}
          withReset={!!selectedYear}
        />
      </Box>
      <Box pad={{ vertical: small ? '0.5rem' : '0' }}>
        <FlexibleSelectDropdown
          textColor={page.inputText}
          handleItemSelect={(value) => setSelectedMonth(value || '')}
          flexibleOptions={MONTH_OPTIONS}
          noSelectionLabel="All months"
          displayKey="display"
          valueKey="api"
          iconPath="images/filter-input-icon.svg"
          customPad="0.2rem 0.3rem"
          selectedOption={MONTH_OPTIONS.find((o) => o.api === selectedMonth)?.display || 'All months'}
          withReset={!!selectedMonth}
        />
      </Box>
      {companyOptions.length > 0 && (
        <Box pad={{ vertical: small ? '0.5rem' : '0' }}>
          <FlexibleSelectDropdown
            textColor={page.inputText}
            handleItemSelect={(value) => setSelectedCompanyId(value || null)}
            flexibleOptions={companyOptions}
            noSelectionLabel="All companies"
            displayKey="display"
            valueKey="api"
            iconPath="images/filter-input-icon.svg"
            customPad="0.2rem 0.3rem"
            selectedOption={
              companyOptions.find((o) => String(o.api) === String(selectedCompanyId || ''))?.display
              || 'All companies'
            }
            withReset={!!selectedCompanyId}
            withSearch
          />
        </Box>
      )}
      <Box pad={{ vertical: small ? '0.5rem' : '0' }}>
        <FlexibleSelectDropdown
          textColor={page.inputText}
          handleItemSelect={(value) => setSelectedStatus(value || '')}
          flexibleOptions={STATUS_OPTIONS}
          noSelectionLabel="All statuses"
          displayKey="display"
          valueKey="api"
          iconPath="images/filter-input-icon.svg"
          customPad="0.2rem 0.3rem"
          selectedOption={STATUS_OPTIONS.find((o) => o.api === selectedStatus)?.display || 'All statuses'}
          withReset={!!selectedStatus}
        />
      </Box>
      <Box pad={{ vertical: small ? '0.5rem' : '0' }}>
        <AppButton
          overrideHover
          disabled={!hasActiveFilters}
          onClick={handleReset}
          level="tableControls"
          height="var(--table-controls-height)"
          color={button.primaryText}
          bgColor={button.primaryBg}
          label="Reset"
        />
      </Box>
    </Box>
  );

  return (
    <AdminNavWrapper
      small={small}
      location={location}
      bannerProps={{
        title: 'Payout Dashboard',
        subTitle: 'Content ID · Admin',
      }}
    >
      <Seo />

      {editEntry && (
        <Layer
          onEsc={() => setEditEntry(null)}
          onClickOutside={() => setEditEntry(null)}
          modal
        >
          <Box
            background={page.altBg}
            pad="1.5rem"
            gap="1rem"
            width={{ min: '28rem', max: '36rem' }}
            round="8px"
          >
            <Text size="1.1rem" weight={600} color={page.primaryText}>
              Edit Payout Amounts
            </Text>
            <Text size="0.9rem" color={page.secondaryText}>
              {`${editEntry.customerName || `Company ${editEntry.companyId}`} · ${editEntry.month}`}
            </Text>

            {(editEntry.assetBreakdown || []).length > 0 && (
              <Box gap="0.5rem">
                <Text size="0.85rem" weight={600} color={page.tableHeader}>
                  Total Revenue per Label
                </Text>
                <Box
                  gap="0.4rem"
                  overflow={{ vertical: 'auto' }}
                  style={{ maxHeight: '14rem' }}
                >
                  {(editEntry.assetBreakdown || []).map((asset) => {
                    const totalVal = parseFloat(editFields.assetUpdates?.[asset.assetLabel] ?? '');
                    const revShare = editEntry.companyRevSharePercent || 0;
                    const calcPayable = !Number.isNaN(totalVal)
                      ? totalVal * (1 - revShare / 100)
                      : null;
                    const calcRfShare = calcPayable !== null ? totalVal - calcPayable : null;
                    return (
                      <Box key={asset.assetLabel} direction="row" align="center" gap="0.75rem">
                        <Box flex>
                          <Text size="0.85rem" color={page.tableText} truncate>
                            {asset.assetLabel || '—'}
                          </Text>
                          {calcPayable !== null && (
                            <Text size="0.75rem" color={page.secondaryText}>
                              {`Payable: ${formatMoney(calcPayable)} · RF: ${formatMoney(calcRfShare)}`}
                            </Text>
                          )}
                        </Box>
                        <Box width="8rem" flex={false}>
                          <TextInput
                            type="number"
                            min="0"
                            step="0.01"
                            value={editFields.assetUpdates?.[asset.assetLabel] ?? ''}
                            onChange={(e) => setEditFields((prev) => ({
                              ...prev,
                              assetUpdates: {
                                ...prev.assetUpdates,
                                [asset.assetLabel]: e.target.value,
                              },
                            }))}
                            placeholder="0.00"
                            style={{
                              background: page.inputBg,
                              color: page.primaryText,
                              border: `1px solid ${page.inputBorder}`,
                              borderRadius: '6px',
                            }}
                          />
                        </Box>
                      </Box>
                    );
                  })}
                </Box>
              </Box>
            )}

            <Box gap="0.3rem">
              <Text size="0.85rem" weight={600} color={page.tableHeader}>Referral Bonus</Text>
              <TextInput
                type="number"
                min="0"
                step="0.01"
                value={editFields.referralBonus}
                onChange={(e) => setEditFields(
                  (prev) => ({ ...prev, referralBonus: e.target.value }),
                )}
                placeholder="0.00"
                style={{
                  background: page.inputBg,
                  color: page.primaryText,
                  border: `1px solid ${page.inputBorder}`,
                  borderRadius: '6px',
                }}
              />
            </Box>

            <Box
              direction="row"
              justify="between"
              pad={{ vertical: '0.5rem', horizontal: '0.75rem' }}
              background={page.tableBg}
              round="6px"
              border={{ color: page.inputBorder, size: '1px' }}
            >
              <Text size="0.85rem" color={page.secondaryText}>Estimated Total Payout</Text>
              <Text size="0.85rem" weight={600} color={page.primaryText}>
                {formatMoney(computeEditTotal(editFields, editEntry?.companyRevSharePercent))}
              </Text>
            </Box>

            <Box direction="row" gap="0.75rem" justify="end">
              <AppButton
                overrideHover
                onClick={() => { setEditEntry(null); setEditFields(EMPTY_EDIT_FIELDS); }}
                level="tableControls"
                color={page.primaryText}
                bgColor={page.inputBg}
                label="Cancel"
              />
              <AppButton
                overrideHover
                disabled={submitting || Object.keys(toSnakeUpdates(editFields)).length === 0}
                onClick={handleEditSave}
                level="tableControls"
                color={button.primaryText}
                bgColor={button.primaryBg}
                label={submitting ? 'Saving...' : 'Save'}
              />
            </Box>
          </Box>
        </Layer>
      )}

      {breakdownEntry && (
        <SimpleModal
          small={small}
          toggleModal={(open) => { if (!open) setBreakdownEntry(null); }}
          modalTitle="Asset Label Breakdown"
          textColor={page.primaryText}
          highlightColor={page.highlight}
          modalBg={page.modalBg}
          closeLabel="Close"
          desktopMinWidth="42rem"
        >
          <Box pad={{ vertical: '0.5rem' }} gap="0.5rem">
            <Text size="0.9rem" color={page.secondaryText}>
              {`${breakdownEntry.customerName || `Company ${breakdownEntry.companyId}`} · ${breakdownEntry.month}`}
            </Text>
            <Box
              direction="row"
              gap="0.5rem"
              pad={{ bottom: '0.5rem' }}
              border={{
                color: page.inputBorder, size: '1px', style: 'solid', side: 'bottom',
              }}
            >
              {['Asset Label', 'Est. Revenue', 'Total Revenue', 'Payable', 'RF Share'].map((h) => (
                <Box key={h} width={h === 'Asset Label' ? '32%' : '17%'}>
                  <Text size="0.75rem" color={page.tableHeader} weight={600}>{h}</Text>
                </Box>
              ))}
            </Box>
            <Box gap="0.75rem">
              {(breakdownEntry.assetBreakdown || []).map((asset) => (
                <Box key={asset.assetLabel} direction="row" gap="0.5rem" align="center">
                  <Box width="32%">
                    <Text size="0.9rem" color={page.tableText} weight={500}>
                      {asset.assetLabel || '—'}
                    </Text>
                  </Box>
                  <Box width="17%">
                    <Text size="0.9rem" color={page.tableText}>{formatMoney(asset.estimatedRevenue)}</Text>
                  </Box>
                  <Box width="17%">
                    <Text size="0.9rem" color={page.tableText}>{formatMoney(asset.totalRevenue)}</Text>
                  </Box>
                  <Box width="17%">
                    <Text size="0.9rem" color={page.tableText}>{formatMoney(asset.payableRevenue)}</Text>
                  </Box>
                  <Box width="17%">
                    <Text size="0.9rem" color={page.tableText}>{formatMoney(asset.redflagShare)}</Text>
                  </Box>
                </Box>
              ))}
            </Box>
          </Box>
        </SimpleModal>
      )}

      {rfShareEntry && (
        <SimpleModal
          small={small}
          toggleModal={(open) => { if (!open) setRfShareEntry(null); }}
          modalTitle="RF Share Breakdown"
          textColor={page.primaryText}
          highlightColor={page.highlight}
          modalBg={page.modalBg}
          closeLabel="Close"
          desktopMinWidth="28rem"
        >
          <Box pad={{ vertical: '0.5rem', horizontal: '1.5rem', bottom: '1.5rem' }} gap="0.75rem">
            <Text size="0.9rem" color={page.secondaryText}>
              {`${rfShareEntry.customerName || `Company ${rfShareEntry.companyId}`} · ${formatMonth(rfShareEntry.month)}`}
            </Text>
            <Box gap="0.4rem">
              {[
                {
                  label: 'Gross RF Share',
                  value: rfShareEntry.redflagShare,
                  color: page.primaryText,
                },
                {
                  label: 'Referral Bonus Paid Out',
                  sublabel: rfShareEntry.referralDeductionTo || 'referrer',
                  value: -(rfShareEntry.referralDeduction || 0),
                  color: page.statusError || '#ef4444',
                },
                {
                  label: 'Net RF Share',
                  value: (rfShareEntry.redflagShare || 0) - (rfShareEntry.referralDeduction || 0),
                  color: page.primaryText,
                  bold: true,
                },
              ].map(({
                label, sublabel, value, color, bold,
              }, i, arr) => (
                <Box
                  key={label}
                  direction="row"
                  justify="between"
                  align="start"
                  pad={{ vertical: '0.5rem', horizontal: '0.75rem' }}
                  background={i === arr.length - 1 ? page.tableBg : 'transparent'}
                  border={i === arr.length - 1 ? { color: page.inputBorder, size: '1px' } : undefined}
                  round={i === arr.length - 1 ? '6px' : undefined}
                >
                  <Box style={{ flex: 1, marginRight: '1rem' }}>
                    <Text size="0.9rem" color={page.secondaryText}>{label}</Text>
                    {sublabel && (
                      <Text size="0.8rem" color={page.secondaryText} style={{ opacity: 0.7 }}>
                        {`to ${sublabel}`}
                      </Text>
                    )}
                  </Box>
                  <Text size="0.9rem" weight={bold ? 700 : 400} color={color} style={{ flexShrink: 0 }}>
                    {value < 0
                      ? `-${formatMoney(Math.abs(value))}`
                      : formatMoney(value)}
                  </Text>
                </Box>
              ))}
            </Box>
          </Box>
        </SimpleModal>
      )}

      {historyEntry && (
        <SimpleModal
          small={small}
          toggleModal={(open) => { if (!open) { setHistoryEntry(null); setHistoryLogs([]); } }}
          modalTitle={`Edit History — ${historyEntry.customerName || `Company ${historyEntry.companyId}`} · ${historyEntry.month}`}
        >
          <Box
            width={{ min: '36rem', max: '56rem' }}
            pad={{ horizontal: '1.5rem', bottom: '1.5rem' }}
            gap="0.75rem"
          >
            {historyLoading && (
              <Text size="0.95rem" color={page.secondaryText}>Loading...</Text>
            )}
            {!historyLoading && historyLogs.length === 0 && (
              <Text size="0.95rem" color={page.secondaryText}>
                No edits have been made to this payout entry.
              </Text>
            )}
            {!historyLoading && historyLogs.map((log) => (
              <Box
                key={`${log.editedAt}-${log.editedBy}`}
                background={page.tableBg}
                border={{ color: page.inputBorder, size: '1px' }}
                round="6px"
                pad="1rem"
                gap="0.5rem"
              >
                <Box direction="row" justify="between" align="center">
                  <Text size="0.8rem" weight={600} color={page.tableHeader}>
                    {`Edited by user #${log.editedBy}`}
                  </Text>
                  <Text size="0.8rem" color={page.secondaryText}>
                    {buildDateTimeLabel(log.editedAt)}
                  </Text>
                </Box>
                {log.changes?.referralBonus && (
                  <Box direction="row" gap="0.5rem" align="center" wrap>
                    <Text size="0.85rem" color={page.tableText} weight={500}>
                      Referral Bonus:
                    </Text>
                    <Text size="0.85rem" color={page.secondaryText}>
                      {formatMoney(log.changes.referralBonus.before)}
                    </Text>
                    <Text size="0.85rem" color={page.secondaryText}>→</Text>
                    <Text size="0.85rem" weight={600} color={page.primaryText}>
                      {formatMoney(log.changes.referralBonus.after)}
                    </Text>
                  </Box>
                )}
                {(log.changes?.assetUpdates || []).map((au) => (
                  <Box
                    key={au.assetLabel}
                    direction="column"
                    gap="0.2rem"
                    pad={{ left: '0.5rem' }}
                    border={{ color: page.inputBorder, size: '1px', side: 'left' }}
                  >
                    <Text size="0.8rem" weight={600} color={page.tableHeader}>
                      {au.assetLabel}
                    </Text>
                    {au.totalRevenue && (
                      <Box direction="row" gap="0.5rem" align="center" wrap>
                        <Text size="0.8rem" color={page.tableText}>Total Revenue:</Text>
                        <Text size="0.8rem" color={page.secondaryText}>
                          {formatMoney(au.totalRevenue?.before)}
                        </Text>
                        <Text size="0.8rem" color={page.secondaryText}>→</Text>
                        <Text size="0.8rem" weight={600} color={page.primaryText}>
                          {formatMoney(au.totalRevenue?.after)}
                        </Text>
                      </Box>
                    )}
                    <Box direction="row" gap="0.5rem" align="center" wrap>
                      <Text size="0.8rem" color={page.tableText}>Payable:</Text>
                      <Text size="0.8rem" color={page.secondaryText}>
                        {formatMoney(au.payableRevenue?.before)}
                      </Text>
                      <Text size="0.8rem" color={page.secondaryText}>→</Text>
                      <Text size="0.8rem" weight={600} color={page.primaryText}>
                        {formatMoney(au.payableRevenue?.after)}
                      </Text>
                    </Box>
                    <Box direction="row" gap="0.5rem" align="center" wrap>
                      <Text size="0.8rem" color={page.tableText}>RF Share:</Text>
                      <Text size="0.8rem" color={page.secondaryText}>
                        {formatMoney(au.redflagShare?.before)}
                      </Text>
                      <Text size="0.8rem" color={page.secondaryText}>→</Text>
                      <Text size="0.8rem" weight={600} color={page.primaryText}>
                        {formatMoney(au.redflagShare?.after)}
                      </Text>
                    </Box>
                  </Box>
                ))}
              </Box>
            ))}
          </Box>
        </SimpleModal>
      )}

      {confirmEntry && (
        <Layer
          onEsc={() => setConfirmEntry(null)}
          onClickOutside={() => setConfirmEntry(null)}
          modal
        >
          <Box
            background={page.altBg}
            pad="1.5rem"
            gap="1rem"
            width={{ min: '22rem', max: '28rem' }}
            round="8px"
          >
            <Text size="1.1rem" weight={600} color={page.primaryText}>
              Confirm Payout
            </Text>
            <Text size="1rem" color={page.primaryText}>
              Trigger payout for
              {' '}
              <strong>{confirmEntry.customerName || `Company ${confirmEntry.companyId}`}</strong>
              {' '}
              for month
              {' '}
              <strong>{confirmEntry.month}</strong>
              ?
            </Text>
            <Text size="0.9rem" color={page.secondaryText}>
              {`Total payout: ${formatMoney(confirmEntry.totalPayout)}`}
            </Text>
            <Box direction="row" gap="0.75rem" justify="end">
              <AppButton
                overrideHover
                onClick={() => setConfirmEntry(null)}
                level="tableControls"
                color={page.primaryText}
                bgColor={page.inputBg}
                label="Cancel"
              />
              <AppButton
                overrideHover
                disabled={submitting}
                onClick={handlePayConfirm}
                level="tableControls"
                color={button.primaryText}
                bgColor={button.primaryBg}
                label={submitting ? 'Processing...' : 'Confirm Pay'}
              />
            </Box>
          </Box>
        </Layer>
      )}

      <Box
        flex
        direction="column"
        background={usePageBg}
        pad={{ vertical: '1.5rem' }}
        height="100%"
        gap="1rem"
        width="100%"
      >
        <InfoNotice
          header="Payout dashboard"
          text="View monthly payout entries for all active CID companies. Use Edit to adjust amounts before triggering a Stripe payout."
        />
        <Box
          direction={small ? 'column' : 'row'}
          gap="1rem"
          pad={{ horizontal: small ? '0' : '1rem' }}
          wrap={false}
        >
          {[
            { label: 'Total Revenue', value: totalRevenueStat },
            { label: 'Estimated Revenue', value: estimatedRevenueStat },
            { label: 'RedFlag Share', value: redflagShareStat },
            { label: 'Total Payable', value: payableRevenueStat },
          ].map(({ label, value }) => (
            <Box
              key={label}
              flex
              background={page.altBg}
              border={{ color: page.highlight, size: '1px' }}
              round="8px"
              pad="1rem"
              gap="0.35rem"
            >
              <Text size="0.8rem" color={page.secondaryText} weight={500}>
                {label}
              </Text>
              <Text size="1.4rem" weight={700} color={page.primaryText}>
                {formatMoney(value)}
              </Text>
            </Box>
          ))}
        </Box>
        <Box width="100%">
          <TableWrapper
            zeroBasedPageIndex
            small={small}
            header="Payout Entries"
            boxPad={tablePadding(small)}
            isPaged={totalResults > limit}
            totalResults={totalResults}
            currentResults={data.length}
            selectedResultsCount={limit}
            currentPage={currentPage}
            handlePage={handlePage}
            overrideNextPageDisabled={skip + limit >= totalResults}
            tableControls={tableControls}
          >
            <Box fill="horizontal" direction="column">
              {renderContents()}
            </Box>
          </TableWrapper>
        </Box>
      </Box>
    </AdminNavWrapper>
  );
};

function mapStateToProps(state) {
  return {
    loading: state.fetchLoader.dataLoading,
    submitting: state.fetchLoader.submissionLoading,
    payoutsData: state.adminPayouts.payouts,
    companyList: state.adminCompany.companies,
  };
}

function mapDispatchToProps(dispatch) {
  return bindActionCreators(
    {
      fetchPayoutsData: initiateFetchPayouts,
      clearPayoutsData: clearPayouts,
      updatePayout: initiateUpdatePayout,
      triggerPayout: initiateTriggerPayout,
      fetchPayoutHistory: initiateFetchPayoutHistory,
      fetchCompanyList: fetchCompanyListRequest,
    },
    dispatch,
  );
}

AdminPayoutDashboardPage.propTypes = {
  small: PropTypes.bool.isRequired,
  location: PropTypes.shape({
    pathname: PropTypes.string.isRequired,
    search: PropTypes.string.isRequired,
    key: PropTypes.string.isRequired,
  }).isRequired,
  fetchPayoutsData: PropTypes.func.isRequired,
  clearPayoutsData: PropTypes.func.isRequired,
  updatePayout: PropTypes.func.isRequired,
  triggerPayout: PropTypes.func.isRequired,
  fetchPayoutHistory: PropTypes.func.isRequired,
  fetchCompanyList: PropTypes.func.isRequired,
  loading: PropTypes.bool.isRequired,
  submitting: PropTypes.bool.isRequired,
  payoutsData: PropTypes.shape({
    data: PropTypes.arrayOf(PropTypes.shape({
      id: PropTypes.string.isRequired,
      companyId: PropTypes.number.isRequired,
      month: PropTypes.string.isRequired,
      customerName: PropTypes.string,
      companyRevSharePercent: PropTypes.number,
      estimatedRevenue: PropTypes.number,
      estimatedDaysCovered: PropTypes.number,
      estimatedDaysExpected: PropTypes.number,
      totalRevenue: PropTypes.number,
      redflagShare: PropTypes.number,
      payableRevenue: PropTypes.number,
      referralBonus: PropTypes.number,
      referralDeduction: PropTypes.number,
      referralDeductionTo: PropTypes.string,
      totalPayout: PropTypes.number,
      status: PropTypes.string,
      paidAt: PropTypes.string,
      receiptUrl: PropTypes.string,
      assetBreakdown: PropTypes.arrayOf(PropTypes.shape({
        assetLabel: PropTypes.string,
        estimatedRevenue: PropTypes.number,
        totalRevenue: PropTypes.number,
        payableRevenue: PropTypes.number,
        redflagShare: PropTypes.number,
      })),
    })),
    pagination: PropTypes.shape({
      total: PropTypes.number,
      skip: PropTypes.number,
      limit: PropTypes.number,
    }),
    totals: PropTypes.shape({
      totalRevenue: PropTypes.number,
      estimatedRevenue: PropTypes.number,
      redflagShare: PropTypes.number,
      payableRevenue: PropTypes.number,
    }),
  }),
  companyList: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
    name: PropTypes.string.isRequired,
  })),
};

export default connect(mapStateToProps, mapDispatchToProps)(AdminPayoutDashboardPage);
